/**
 * Signup Page
 *
 * Registration form with email verification flow.
 * Blueprint Task: 2.2.5
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { PORTAL_BRAND } from '../config/branding';
import { useAuth } from '../providers/AuthProvider';
import { linkGuestOrders } from '../services/orderService';

type SignupStep = 'register' | 'verify';

/**
 *
 */
export function Signup() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId') ?? undefined;
  const [step, setStep] = useState<SignupStep>('register');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { register, confirmSignup, login, logout } = useAuth();
  const navigate = useNavigate();

  // Password strength calculation
  function getPasswordStrength(password: string): { score: number; label: string; color: string } {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: 'hsl(var(--color-error-500))' };
    if (score <= 2) return { score, label: 'Fair', color: 'hsl(var(--color-warning-500))' };
    if (score <= 3) return { score, label: 'Good', color: 'hsl(var(--color-warning-400))' };
    return { score, label: 'Strong', color: 'hsl(var(--color-success-500))' };
  }

  const passwordStrength = getPasswordStrength(password);

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!acceptTerms) {
      setError('Please accept the Terms of Service.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(email, password, firstName, lastName);
      if (result.needsConfirmation) {
        setStep('verify');
      } else {
        // Auto-login if no confirmation needed
        await login(email, password);
        // Link any guest orders to this new user account
        try {
          const result = await linkGuestOrders(email, orderId);
          if (result.linkedCount > 0) {
            console.info(`Linked ${result.linkedCount} guest order(s) to account`);
          }
        } catch (linkError) {
          // Non-blocking - don't prevent navigation if linking fails
          console.warn('Failed to link guest orders:', linkError);
        }
        void navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const error = err as { message?: string; code?: string };
      if (error.code === 'UsernameExistsException') {
        setError('An account with this email already exists.');
      } else {
        setError(error.message ?? 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await confirmSignup(email, verificationCode);

      // Auto-login: clear any stale Amplify session, then sign in.
      // Wrap in try/catch so verification success is never lost — if
      // auto-login fails, redirect to /login with a success message.
      try {
        try { await logout(); } catch { /* no session to clear */ }
        await login(email, password);
        // Link any guest orders to this new user account
        try {
          const result = await linkGuestOrders(email, orderId);
          if (result.linkedCount > 0) {
            console.info(`Linked ${result.linkedCount} guest order(s) to account`);
          }
        } catch (linkError) {
          console.warn('Failed to link guest orders:', linkError);
        }
        void navigate('/dashboard', { replace: true });
      } catch {
        // Auto-login failed (session race, Amplify state issue, etc.)
        // Verification succeeded — send user to login with pre-filled email
        const params = new URLSearchParams();
        params.set('email', email);
        params.set('verified', '1');
        if (orderId) params.set('orderId', orderId);
        void navigate(`/login?${params.toString()}`, { replace: true });
      }
    } catch (err) {
      const error = err as { message?: string; code?: string };
      if (error.code === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else {
        setError(error.message ?? 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (step === 'verify') {
    return (
      <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <a href={PORTAL_BRAND.websiteUrl} className="flex justify-center">
            <img src={PORTAL_BRAND.logoUrl} alt={PORTAL_BRAND.companyName} className="h-10" />
          </a>
          <a
            href={PORTAL_BRAND.websiteUrl}
            className="mt-1 block text-center text-sm"
            style={{ color: 'hsl(var(--color-neutral-400))' }}
          >
            &larr; {PORTAL_BRAND.websiteLabel}
          </a>
          <h2
            className="mt-6 text-center text-2xl font-heading font-medium tracking-tight"
            style={{ color: 'hsl(var(--color-neutral-900))' }}
          >
            Verify your email
          </h2>
          <p
            className="mt-2 text-center text-sm"
            style={{ color: 'hsl(var(--color-neutral-600))' }}
          >
            We sent a verification code to <strong>{email}</strong>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div
            className="rounded-lg px-4 py-8 shadow sm:px-10"
            style={{ backgroundColor: 'hsl(var(--color-neutral-50))' }}
          >
            {error && (
              <div
                className="mb-4 rounded-md p-3 text-sm"
                style={{
                  backgroundColor: 'hsl(var(--color-error-50))',
                  color: 'hsl(var(--color-error-700))',
                }}
                role="alert"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <label
                  htmlFor="code"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Verification code
                </label>
                <div className="mt-1">
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 text-center text-2xl tracking-widest placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                style={{
                  backgroundColor: 'hsl(var(--color-primary-600))',
                }}
              >
                {isLoading ? 'Verifying...' : 'Verify email'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <a href={PORTAL_BRAND.websiteUrl} className="flex justify-center">
            <img src={PORTAL_BRAND.logoUrl} alt={PORTAL_BRAND.companyName} className="h-10" />
          </a>
          <a
            href={PORTAL_BRAND.websiteUrl}
          className="mt-1 block text-center text-sm"
          style={{ color: 'hsl(var(--color-neutral-400))' }}
        >
            &larr; {PORTAL_BRAND.websiteLabel}
        </a>
        <h2
          className="mt-6 text-center text-2xl font-heading font-medium tracking-tight"
          style={{ color: 'hsl(var(--color-neutral-900))' }}
        >
          Create your account
        </h2>
        <p
          className="mt-2 text-center text-sm"
          style={{ color: 'hsl(var(--color-neutral-600))' }}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium underline"
            style={{ color: 'hsl(var(--color-primary-600))' }}
          >
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div
          className="rounded-lg px-4 py-8 shadow sm:px-10"
          style={{ backgroundColor: 'hsl(var(--color-neutral-50))' }}
        >
          {error && (
            <div
              className="mb-4 rounded-md p-3 text-sm"
              style={{
                backgroundColor: 'hsl(var(--color-error-50))',
                color: 'hsl(var(--color-error-700))',
              }}
              role="alert"
            >
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleRegister}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  First name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium"
                  style={{ color: 'hsl(var(--color-neutral-700))' }}
                >
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                    style={{
                      borderColor: 'hsl(var(--color-neutral-300))',
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-700))' }}
              >
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                  style={{
                    borderColor: 'hsl(var(--color-neutral-300))',
                  }}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-700))' }}
              >
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                  style={{
                    borderColor: 'hsl(var(--color-neutral-300))',
                  }}
                />
              </div>
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className="h-1 flex-1 rounded"
                        style={{
                          backgroundColor:
                            level <= passwordStrength.score
                              ? passwordStrength.color
                              : 'hsl(var(--color-neutral-200))',
                        }}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-xs" style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-700))' }}
              >
                Confirm password
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full appearance-none rounded-md border px-3 py-2 placeholder-brand-neutral-400 shadow-sm focus:outline-hidden focus:ring-2 sm:text-sm"
                  style={{
                    borderColor: 'hsl(var(--color-neutral-300))',
                  }}
                />
              </div>
            </div>

            <div className="flex items-start">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-brand-neutral-300"
                style={{ accentColor: 'hsl(var(--color-primary-600))' }}
              />
              <label
                htmlFor="terms"
                className="ml-2 block text-sm"
                style={{ color: 'hsl(var(--color-neutral-700))' }}
              >
                I agree to the{' '}
                <a
                  href="/terms"
                  className="underline"
                  style={{ color: 'hsl(var(--color-primary-600))' }}
                  target="_blank"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/privacy"
                  className="underline"
                  style={{ color: 'hsl(var(--color-primary-600))' }}
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{
                backgroundColor: 'hsl(var(--color-primary-600))',
              }}
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <SocialLoginButtons dividerText="or sign up with" />
        </div>
      </div>
    </div>
  );
}
